import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Clock, LogIn, LogOut, MapPin, ShieldCheck, Repeat } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { api } from "../lib/api";
import PunchQuestions from "../components/PunchQuestions";

/**
 * TIME-1 §2/§3 — the clock screen. Nothing on this page ever punches anybody: every punch is a
 * tap. Location is only read while the clock is running, and only after this account has accepted
 * the consent notice.
 */
const CONSENT_TEXT =
  "While you are clocked in, this app records your location — where you punch in and out, and a "
  + "breadcrumb every few minutes — so your hours can be checked against where the work happened. "
  + "It records nothing when you are off the clock. The record is evidence for your timesheet; it "
  + "never pays you by itself, and payroll is always calculated in Miter.";

const hhmm = (iso) => (iso ? new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }) : "—");

export default function TimeClockPage() {
  const [state, setState] = useState(null);
  const [cards, setCards] = useState([]);
  const [ack, setAck] = useState([]);
  const [moved, setMoved] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [prompt, setPrompt] = useState(null);
  const [askByScope, setAskByScope] = useState({});
  const [picked, setPicked] = useState(null);
  const [answers, setAnswers] = useState({});
  const crumbTimer = useRef(null);

  const load = useCallback(async () => {
    const [{ data: s }, { data: land }] = await Promise.all([
      api.get("/time/open"),
      api.get("/install/landing").catch(() => ({ data: {} })),
    ]);
    setState(s);
    const mobs = await api.get("/mobilizations", { params: { mine: 1 } })
      .then((r) => r.data || []).catch(() => []);
    const today = new Date().toISOString().slice(0, 10);
    const mine = mobs.filter((m) => (m.scheduled_start_date || "") <= today
      && (m.scheduled_end_date || m.scheduled_start_date || "") >= today);
    const list = mine.length ? mine : (land.mob_id ? [{ id: land.mob_id, project_id: land.project_id,
      title: land.label, scope_type: land.scope_type }] : []);
    setCards(list);
    // Today's clock-in set, per trade. Empty everywhere = nothing to ask.
    const codes = [...new Set(list.map((c) => (c.scope_type || "").toUpperCase()).filter(Boolean))];
    const { data: q } = await api.get("/time-questions/ask", {
      params: { kind: "safety", scope_codes: codes.join(",") || "*" },
    }).catch(() => ({ data: { by_scope: {} } }));
    setAskByScope(q.by_scope || {});
  }, []);

  const questionsFor = (card) => askByScope[(card?.scope_type || "").toUpperCase()]
    || askByScope["*"] || [];
  const answerList = (qs) => qs.map((q) => ({ question_id: q.id, ...(answers[q.id] || {}) }))
    .map((a) => ({ question_id: a.question_id, answer: a.answer || "",
                   note: a.note || "", photo_path: a.photo_path || "" }));

  useEffect(() => { load(); }, [load]);

  const pos = () => new Promise((resolve) => {
    if (!navigator.geolocation) return resolve({});
    navigator.geolocation.getCurrentPosition(
      (p) => resolve({ lat: p.coords.latitude, lng: p.coords.longitude, accuracy_m: p.coords.accuracy }),
      () => resolve({}), { enableHighAccuracy: true, timeout: 8000 });
  });

  // §4 — breadcrumbs run ONLY while clocked in, and stop the moment the punch closes.
  useEffect(() => {
    const on = !!state?.entry && !state?.entry?.clock_out && !!state?.consent;
    if (crumbTimer.current) { clearInterval(crumbTimer.current); crumbTimer.current = null; }
    if (!on) return undefined;
    const every = Math.max(1, state.breadcrumb_interval_minutes || 5) * 60 * 1000;
    const send = async () => {
      const p = await pos();
      if (p.lat === undefined) return;
      api.post("/time/observations", { observations: [{ at: new Date().toISOString(), ...p }] })
        .catch(() => {});
    };
    send();
    crumbTimer.current = setInterval(send, every);
    return () => { if (crumbTimer.current) clearInterval(crumbTimer.current); };
  }, [state]);

  // §4 — a crossing off the clock asks a question. It never punches.
  const checkFence = async () => {
    const p = await pos();
    if (p.lat === undefined) { toast.error("Location is not available on this device"); return; }
    const { data } = await api.post("/time/fence-check", { lat: p.lat, lng: p.lng });
    setPrompt(data.prompt);
    if (!data.prompt) toast.info(data.inside ? `Inside ${data.inside.label}` : "Not near a site fence");
  };

  const clockIn = async (card) => {
    setBusy(true);
    try {
      const p = await pos();
      const { data } = await api.post("/time/clock-in", {
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        project_id: card.project_id, mobilization_id: card.id,
        clock_in: new Date().toISOString(), device: navigator.userAgent.slice(0, 60),
        safety_ack: ack, answers: answerList(questionsFor(card)), notes: note.trim(), ...p,
      });
      const said = (data.entry.clockin_answers || []).filter((a) => a.flagged);
      toast.success("Clocked in", {
        description: said.length ? `${hhmm(data.entry.clock_in)} — your foreman has been told`
                                 : hhmm(data.entry.clock_in),
      });
      setAck([]); setNote(""); setPrompt(null); setPicked(null); setAnswers({});
      await load();
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d?.photo_required) toast.error(typeof d.detail === "string" ? d.detail : "A photo is needed");
      else if (d?.safety_required) toast.error("Answer today's questions first");
      else if (d?.consent_required) toast.error("Accept the location notice first");
      else toast.error(typeof d === "string" ? d : d?.detail || "Could not clock in");
    } finally { setBusy(false); }
  };

  const clockOut = async () => {
    setBusy(true);
    try {
      const p = await pos();
      const { data } = await api.post("/time/clock-out", {
        entry_id: state.entry.id, clock_out: new Date().toISOString(),
        moved_material: moved, answers: answerList(state.clockout_questions || []),
        notes: note.trim(), ...p,
      });
      const flags = data.entry.flags || [];
      toast.success("Clocked out", {
        description: flags.length
          ? `${(data.entry.worked_hours ?? 0)} h — ${flags.length} thing(s) for your foreman to look at`
          : `${(data.entry.worked_hours ?? 0)} h recorded`,
      });
      setMoved(null); setNote(""); setAnswers({});
      await load();
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d?.photo_required) toast.error(typeof d.detail === "string" ? d.detail : "A photo is needed");
      else if (d?.material_question_required) toast.error("Answer the clock-out questions first");
      else toast.error(typeof d === "string" ? d : d?.detail || "Could not clock out");
    } finally { setBusy(false); }
  };

  const consent = async (accepted) => {
    await api.post("/time/consent", { accepted, text_shown: CONSENT_TEXT });
    toast[accepted ? "success" : "info"](accepted ? "Thanks — noted with today's date"
                                                  : "Consent withdrawn");
    await load();
  };

  if (!state) return null;
  if (!state.enabled) {
    return (
      <div className="p-6 max-w-lg mx-auto text-center" data-testid="timeclock-off">
        <Clock className="w-8 h-8 mx-auto text-gray-300" />
        <h1 className="mt-2 text-lg font-semibold text-gray-900">Time clock is off</h1>
        <p className="mt-1 text-sm text-gray-500">
          An administrator turns it on in Settings &gt; Field &gt; Timekeeping. Nothing is recorded
          until then.
        </p>
      </div>
    );
  }

  const open = state.entry && !state.entry.clock_out ? state.entry : null;

  return (
    <div className="p-3 sm:p-4 max-w-lg space-y-3" data-testid="timeclock-page">
      <h1 className="text-lg font-semibold text-gray-900 sm:hidden">Time clock</h1>

      {!state.consent && (
        <section className="rounded-lg border border-amber-300 bg-amber-50 p-3" data-testid="timeclock-consent">
          <h2 className="text-sm font-semibold text-amber-900 flex items-center gap-1">
            <MapPin className="w-4 h-4" /> Before you clock in
          </h2>
          <p className="mt-1 text-xs text-amber-900 leading-relaxed">{CONSENT_TEXT}</p>
          <div className="mt-2 flex gap-2">
            <Button size="sm" onClick={() => consent(true)} data-testid="timeclock-consent-accept">
              I understand
            </Button>
            <Button size="sm" variant="outline" onClick={() => consent(false)}
                    data-testid="timeclock-consent-decline">Not now</Button>
          </div>
        </section>
      )}

      {prompt && (
        <section className="rounded-lg border border-blue-300 bg-blue-50 p-3" data-testid="timeclock-fence-prompt">
          <p className="text-sm text-blue-900 font-semibold">{prompt.message}</p>
          <p className="text-[11px] text-blue-800 mt-0.5">
            Nothing has been recorded — this is only a reminder.
          </p>
          <div className="mt-2 flex gap-2">
            {prompt.kind === "clock_out" && (
              <Button size="sm" onClick={clockOut} disabled={busy}
                      data-testid="timeclock-prompt-clock-out">Clock out</Button>
            )}
            <Button size="sm" variant="outline" onClick={() => setPrompt(null)}
                    data-testid="timeclock-prompt-dismiss">Not yet</Button>
          </div>
        </section>
      )}

      {open ? (
        <section className="rounded-lg border border-emerald-300 bg-emerald-50 p-4" data-testid="timeclock-open">
          <p className="text-xs uppercase tracking-wide text-emerald-800">On the clock since</p>
          <p className="text-3xl font-semibold tabular-nums text-emerald-900">{hhmm(open.clock_in)}</p>
          <p className="mt-0.5 text-sm text-emerald-900">
            {open.job_number} {open.project_name} · {open.scope_type || "—"}
          </p>
          {state.material_questions && (state.clockout_questions || []).length > 0 && (
            <div className="mt-3" data-testid="timeclock-clockout-questions">
              <p className="text-sm font-semibold text-gray-900">Before you go</p>
              <div className="mt-1.5">
                <PunchQuestions questions={state.clockout_questions} answers={answers}
                                onChange={setAnswers} projectId={open.project_id}
                                prefix="timeclock-out" />
              </div>
            </div>
          )}
          {state.material_questions && (state.clockout_questions || []).length === 0 && (
            <div className="mt-3" data-testid="timeclock-material-question">
              <p className="text-sm font-semibold text-gray-900">Did you move material today?</p>
              <div className="mt-1 flex gap-2">
                {[["yes", true], ["no", false]].map(([label, v]) => (
                  <button key={label} type="button" onClick={() => setMoved(v)}
                          data-testid={`timeclock-moved-${label}`}
                          className={`h-9 px-4 rounded-md border text-sm font-semibold ${
                            moved === v ? "bg-gray-900 text-white border-gray-900"
                                        : "bg-white border-gray-300 text-gray-700"}`}>
                    {label === "yes" ? "Yes" : "No"}
                  </button>
                ))}
              </div>
              {moved === true && (
                <p className="mt-1 text-[11px] text-gray-600">
                  Say where it went on the Material view — that move is recorded there, on the same
                  record the installer reads.
                </p>
              )}
            </div>
          )}
          <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2}
                    placeholder="Anything worth noting (optional)"
                    className="mt-3 text-sm" data-testid="timeclock-note" />
          <div className="mt-2 flex gap-2">
            <Button className="flex-1 h-11" onClick={clockOut} disabled={busy}
                    data-testid="timeclock-clock-out">
              <LogOut className="w-4 h-4 mr-1" /> Clock out
            </Button>
            <Button variant="outline" className="h-11" onClick={checkFence}
                    data-testid="timeclock-fence-check" title="Am I on site?">
              <MapPin className="w-4 h-4" />
            </Button>
          </div>
        </section>
      ) : (
        <section className="space-y-2" data-testid="timeclock-cards">
          {state.safety_prompts && state.safety_questions.length > 0
            && !Object.values(askByScope).some((a) => (a || []).length) && (
            <div className="rounded-lg border border-gray-200 bg-white p-3" data-testid="timeclock-safety">
              <h2 className="text-sm font-semibold text-gray-900 flex items-center gap-1">
                <ShieldCheck className="w-4 h-4 text-emerald-600" /> Before you start
              </h2>
              {state.safety_questions.map((q) => (
                <label key={q} className="mt-1.5 flex items-start gap-2 text-sm text-gray-700"
                       data-testid="timeclock-safety-question">
                  <input type="checkbox" className="mt-1" checked={ack.includes(q)}
                         onChange={(e) => setAck((a) => e.target.checked ? [...a, q]
                                                                        : a.filter((x) => x !== q))} />
                  <span>{q}</span>
                </label>
              ))}
            </div>
          )}

          {picked ? (
            <div className="rounded-lg border border-gray-200 bg-white p-3" data-testid="timeclock-punch-questions">
              <h2 className="text-sm font-semibold text-gray-900 flex items-center gap-1">
                <ShieldCheck className="w-4 h-4 text-emerald-600" /> Before you start ·{" "}
                {picked.title || picked.scope_type || "Dispatch card"}
              </h2>
              <div className="mt-2">
                <PunchQuestions questions={questionsFor(picked)} answers={answers}
                                onChange={setAnswers} projectId={picked.project_id}
                                prefix="timeclock-in" />
              </div>
              <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2}
                        placeholder="Anything your foreman should know (optional)"
                        className="mt-2 text-sm" data-testid="timeclock-in-note" />
              <div className="mt-2 flex gap-2">
                <Button className="flex-1 h-11" disabled={busy || !state.consent}
                        onClick={() => clockIn(picked)} data-testid="timeclock-confirm-clock-in">
                  <LogIn className="w-4 h-4 mr-1" /> Clock in
                </Button>
                <Button variant="outline" className="h-11" disabled={busy}
                        onClick={() => { setPicked(null); setAnswers({}); }}
                        data-testid="timeclock-punch-cancel">Back</Button>
              </div>
            </div>
          ) : cards.length === 0 ? (
            <p className="text-sm text-gray-500 border border-dashed border-gray-300 rounded-lg p-6 text-center"
               data-testid="timeclock-no-cards">
              Nothing is dispatched to you today. Ask your foreman to put you on a card.
            </p>
          ) : cards.map((c) => (
            <button key={c.id} type="button" disabled={busy || !state.consent}
                    onClick={() => (questionsFor(c).length ? setPicked(c) : clockIn(c))}
                    data-testid={`timeclock-clock-in-${c.id}`}
                    className="w-full text-left rounded-lg border border-gray-200 bg-white p-3 hover:border-emerald-400 disabled:opacity-50">
              <span className="flex items-center gap-2">
                <LogIn className="w-4 h-4 text-emerald-600" />
                <b className="text-sm text-gray-900">{c.title || c.scope_type || "Dispatch card"}</b>
                <span className="ml-auto text-xs font-semibold text-emerald-700">Clock in</span>
              </span>
              <span className="block mt-0.5 text-xs text-gray-500">
                {c.scope_type} {c.building ? `· Building ${c.building}` : ""}
                {c.floor ? ` · Floor ${c.floor}` : ""}{c.area ? ` · Area ${c.area}` : ""}
              </span>
            </button>
          ))}
          {!picked && cards.length > 1 && (
            <p className="text-[11px] text-gray-500 flex items-center gap-1">
              <Repeat className="w-3 h-3" /> More than one card today — pick the one you are
              starting on. Switching later is one tap.
            </p>
          )}
        </section>
      )}
    </div>
  );
}
